"use client";

import { Spinner } from "@/components/ui/spinner";

import { useEffect, useEffectEvent, useState } from "react";
import { AlertCircle, CheckCircle2, RefreshCw, ServerCog } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatTimeAgo, parseDateValue } from "@/lib/time-ago";
import {
  RpaMonitorService,
  type RpaMonitorConfigDto,
  type RpaWorkerStatus,
  type RpaWorkerStatusDto,
} from "@/lib/rpa-monitor-service";
import { cn } from "@/lib/utils";

const STATUS_LABELS = {
  OK: "OK",
  STALE: "Sin heartbeat",
  DOWN: "Caído",
  ERP_NOT_READY: "ERP no listo",
  BUSY_TOO_LONG: "Tarea bloqueada",
  ERROR: "Error",
} satisfies Record<RpaWorkerStatus, string>;

const STATUS_BADGE_CLASS = {
  OK: "bg-emerald-100 text-emerald-800",
  STALE: "bg-amber-100 text-amber-800",
  DOWN: "bg-red-100 text-red-800",
  ERP_NOT_READY: "bg-orange-100 text-orange-800",
  BUSY_TOO_LONG: "bg-purple-100 text-purple-800",
  ERROR: "bg-red-100 text-red-800",
} satisfies Record<RpaWorkerStatus, string>;

async function fetchRpaMonitorSnapshot() {
  const [workers, config] = await Promise.all([
    RpaMonitorService.getWorkers(),
    RpaMonitorService.getMonitorConfig().catch(() => null),
  ]);
  return { workers, config };
}

export default function RpaMonitorPage() {
  const [workers, setWorkers] = useState<RpaWorkerStatusDto[]>([]);
  const [monitorConfig, setMonitorConfig] = useState<RpaMonitorConfigDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadWorkers = async () => {
    try {
      const { workers: nextWorkers, config } = await fetchRpaMonitorSnapshot();
      setWorkers(nextWorkers);
      setMonitorConfig(config);
      setError(null);
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "Error cargando monitor RPA";
      setError(message);
    }
    setIsLoading(false);
  };
  const loadWorkersInEffect = useEffectEvent(loadWorkers);

  useEffect(() => {
    const initialLoadTimeout = window.setTimeout(() => {
      void loadWorkersInEffect();
    }, 0);
    const interval = window.setInterval(() => {
      void loadWorkersInEffect();
    }, 30000);
    return () => {
      window.clearTimeout(initialLoadTimeout);
      window.clearInterval(interval);
    };
  }, []);

  const workersWithIssues = workers.filter((worker) => worker.status !== "OK");
  const healthyCount = workers.length - workersWithIssues.length;

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Monitor RPA</h1>
            <p className="text-sm text-gray-600">
              Estado operativo de los workers Windows, NET4 y heartbeats.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setError(null);
              void loadWorkers();
            }}
            disabled={isLoading}
          >
            {isLoading ? <Spinner className="mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Actualizar
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No se pudo cargar el monitor</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-4">
          <SummaryCard title="Workers registrados" value={workers.length} />
          <SummaryCard title="Operativos" value={healthyCount} tone="success" />
          <SummaryCard title="Con revisión" value={workersWithIssues.length} tone="danger" />
          <ConfigCard config={monitorConfig} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ServerCog className="h-5 w-5" />
              Estado actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workers.length === 0 && !isLoading ? (
              <Empty className="border p-8 md:p-8">
                <EmptyHeader className="gap-2">
                  <EmptyMedia>
                    <ServerCog className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle className="text-sm font-medium tracking-normal text-gray-700">
                    Todavía no hay heartbeats registrados.
                  </EmptyTitle>
                  <EmptyDescription className="text-sm text-gray-600">
                    El worker aparecerá aquí cuando envíe la primera señal.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Último heartbeat</TableHead>
                    <TableHead>NET4</TableHead>
                    <TableHead>Sesión</TableHead>
                    <TableHead>Tarea</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workers.map((worker) => (
                    <TableRow key={worker.workerId}>
                      <TableCell>
                        <div className="font-medium">{worker.workerId}</div>
                        <div className="text-xs text-gray-500">
                          {worker.hostname ?? "Sin host"} {worker.vmAddress ? `- ${worker.vmAddress}` : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={worker.status} />
                        {worker.lastError && (
                          <div className="mt-1 max-w-72 truncate text-xs text-red-700">
                            {worker.lastError}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{formatNullableDate(worker.lastHeartbeatAt)}</TableCell>
                      <TableCell>{booleanLabel(worker.erpReady)}</TableCell>
                      <TableCell>{booleanLabel(worker.sessionReady)}</TableCell>
                      <TableCell>
                        {worker.taskInProgress ? (
                          <span className="text-amber-700">En curso</span>
                        ) : (
                          <span className="text-gray-600">Idle</span>
                        )}
                        {worker.lastTaskId && (
                          <div className="text-xs text-gray-500">{worker.lastTaskId}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ConfigCard({ config }: { config: RpaMonitorConfigDto | null }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-gray-600">Worker configurado</p>
        <p className="mt-2 truncate text-xl font-bold text-gray-900">
          {config?.monitoredWorkerId ?? "Sin datos"}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {config
            ? config.source === "RPA_MONITOR_WORKER_ID"
              ? "Desde RPA_MONITOR_WORKER_ID"
              : "Valor por defecto"
            : "Sin datos del backend"}
        </p>
      </CardContent>
    </Card>
  );
}

function SummaryCard({
  title,
  value,
  tone = "neutral",
}: {
  title: string;
  value: number;
  tone?: "neutral" | "success" | "danger";
}) {
  const iconClass =
    tone === "success" ? "text-emerald-600" : tone === "danger" ? "text-red-600" : "text-gray-600";

  return (
    <Card>
      <CardContent className="flex items-center justify-between pt-6">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <CheckCircle2 className={cn("h-8 w-8", iconClass)} />
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: RpaWorkerStatus }) {
  return (
    <Badge variant="outline" className={STATUS_BADGE_CLASS[status]}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

function formatNullableDate(value: string | null) {
  if (!value) {
    return <span className="text-gray-500">Sin datos</span>;
  }

  const date = parseDateValue(value);
  if (!date) {
    return <span className="text-red-700">Fecha no valida</span>;
  }

  return (
    <div>
      <div>{formatTimeAgo(value)}</div>
      <div className="text-xs text-gray-500">{date.toLocaleString()}</div>
    </div>
  );
}

function booleanLabel(value: boolean | null) {
  if (value === null) {
    return <span className="text-gray-500">Sin datos</span>;
  }
  return value ? <span className="text-emerald-700">OK</span> : <span className="text-red-700">No</span>;
}
